import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, Form, Input, InputNumber, Switch, Button, message, Spin, Table, Tag, Image, Modal, Popconfirm } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getExploreCategory } from '../../graphql/queries';
import { updateExploreCategory, createExploreAudio, deleteExploreAudio } from '../../graphql/mutation';
import '../Audios/Audios.css';
import './Explore.css';

const CategoryDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [audioForm] = Form.useForm();
  const [audioModalVisible, setAudioModalVisible] = useState(false);

  const { loading: queryLoading, data, refetch } = useQuery(getExploreCategory, {
    variables: { id },
    skip: !id
  });

  const [updateCategory, { loading: updateLoading }] = useMutation(updateExploreCategory, {
    onCompleted: () => {
      message.success('Category updated successfully');
    },
    onError: (error) => message.error(error.message)
  });

  const [createAudio, { loading: createAudioLoading }] = useMutation(createExploreAudio, {
    onCompleted: () => {
      message.success('Audio added successfully');
      setAudioModalVisible(false);
      audioForm.resetFields();
      refetch();
    },
    onError: (error) => message.error(error.message)
  });

  const [removeAudio, { loading: deleteAudioLoading }] = useMutation(deleteExploreAudio, {
    onCompleted: () => {
      message.success('Audio removed successfully');
      refetch();
    },
    onError: (error) => message.error(error.message)
  });

  const category = data?.getExploreCategory;

  useEffect(() => {
    if (category) {
      form.setFieldsValue({
        name: category.name,
        order: category.order,
        isActive: category.isActive
      });
    }
  }, [category, form]);

  const onFinish = (values) => {
    updateCategory({
      variables: {
        id,
        input: {
          name: values.name,
          order: values.order,
          isActive: values.isActive
        }
      }
    });
  };

  const handleAddAudio = async () => {
    try {
      const values = await audioForm.validateFields();
      createAudio({
        variables: {
          input: {
            categoryId: id,
            name: values.name,
            imageUrl: values.imageUrl,
            audioUrl: values.audioUrl,
            prompt: values.prompt || '',
            lyrics: values.lyrics || '',
            order: values.order || 0,
            isActive: values.isActive !== false
          }
        }
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleDeleteAudio = (audioId) => {
    removeAudio({
      variables: { id: audioId }
    });
  };

  const audioColumns = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (url) => (
        <Image
          src={url}
          alt="Audio"
          width={50}
          height={50}
          style={{ borderRadius: 8, objectFit: 'cover' }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
        />
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      align: 'center'
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title="Remove this audio?"
          description="This action cannot be undone."
          onConfirm={() => handleDeleteAudio(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            loading={deleteAudioLoading}
          />
        </Popconfirm>
      )
    }
  ];

  if (queryLoading) {
    return (
      <div className="explore-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="audios-container">
      <div className="audios-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/explore')}
          style={{ marginBottom: 16 }}
        >
          Back to Explore
        </Button>
        <h1 className="audios-title">Edit Category: {category?.name}</h1>
      </div>

      <Card className="audios-card" style={{ maxWidth: 600, marginBottom: 24 }}>
        <h3 style={{ marginBottom: 16 }}>Category Settings</h3>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input placeholder="Category name" />
          </Form.Item>

          <Form.Item
            name="order"
            label="Order"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Display order" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={updateLoading}>
              Update Category
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card className="audios-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Category Audios ({category?.audios?.length || 0})</h3>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAudioModalVisible(true)}
          >
            Add Audio
          </Button>
        </div>
        <Table
          columns={audioColumns}
          dataSource={category?.audios || []}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} audios`
          }}
        />
      </Card>

      <Modal
        title="Add New Audio"
        open={audioModalVisible}
        onOk={handleAddAudio}
        onCancel={() => {
          setAudioModalVisible(false);
          audioForm.resetFields();
        }}
        confirmLoading={createAudioLoading}
        okText="Add"
        cancelText="Cancel"
        width={600}
      >
        <Form form={audioForm} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter audio name' }]}
          >
            <Input placeholder="Audio name" />
          </Form.Item>

          <Form.Item
            name="imageUrl"
            label="Image URL"
            rules={[{ required: true, message: 'Please enter image URL' }]}
          >
            <Input placeholder="https://example.com/image.jpg" />
          </Form.Item>

          <Form.Item
            name="audioUrl"
            label="Audio URL"
            rules={[{ required: true, message: 'Please enter audio URL' }]}
          >
            <Input placeholder="https://example.com/audio.mp3" />
          </Form.Item>

          <Form.Item
            name="prompt"
            label="Prompt"
          >
            <Input.TextArea rows={3} placeholder="Audio prompt (optional)" />
          </Form.Item>

          <Form.Item
            name="lyrics"
            label="Lyrics"
          >
            <Input.TextArea rows={3} placeholder="Lyrics (optional)" />
          </Form.Item>

          <Form.Item
            name="order"
            label="Order"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Display order" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch defaultChecked />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryDetail;
